import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import { useAvailability } from '../hooks/useAvailability';

export default function ArtistProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const { slots } = useAvailability();

  useEffect(() => {
    async function fetchArtist() {
      setLoading(true);
      try {
        const data = await api.get(`/artists/${slug}`);
        setArtist(data.data.profile || data.data);
      } catch (err) {
        setError(err.status === 404 ? 'Artiste introuvable' : err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchArtist();
  }, [slug]);

  if (loading) {
    return (
      <div className="animate-pulse max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="w-36 h-36 sm:w-44 sm:h-44 th-bg-elevated shrink-0" />
          <div className="flex-1 space-y-4 pt-2">
            <div className="h-10 w-2/3 th-bg-elevated" />
            <div className="h-4 w-40 th-bg-elevated" />
            <div className="h-6 w-52 th-bg-elevated" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <p className="th-text-secondary text-lg mb-4">{error}</p>
        <Link to="/artists" className="th-link-mono">
          &larr; Retour aux artistes
        </Link>
      </div>
    );
  }

  if (!artist) return null;

  const isPromoter = isAuthenticated && user?.role === 'promoter';
  const isVisitor = !isAuthenticated;
  const initial = artist.artistName?.charAt(0)?.toUpperCase();

  // Normalisation des genres (string ou objet)
  const genres = Array.isArray(artist.genres) ? artist.genres : [];
  const genreName = (g) => (typeof g === 'string' ? g : g?.name);
  const isPrimaryGenre = (g) => typeof g !== 'string' && g?.isPrimary === true;
  const primaryGenre =
    genres.find((g) => isPrimaryGenre(g)) || genres[0] || null;

  const socialEntries = artist.socialLinks
    ? Object.entries(artist.socialLinks)
    : [];

  const slotStatusLabel = (status) =>
    status === 'available'
      ? 'Disponible'
      : status === 'booked'
        ? 'Réservé'
        : status === 'pending'
          ? 'En attente'
          : 'Bloqué';

  const goToBooking = () =>
    navigate(
      `/dashboard?action=book&artistId=${artist.id}&artistName=${encodeURIComponent(artist.artistName)}`
    );

  // Lignes d'informations dérivées (uniquement si données présentes)
  const infoRows = [];
  const baseParts = [artist.location?.city, artist.location?.country].filter(Boolean);
  if (baseParts.length) infoRows.push(['Base', baseParts.join(' · ')]);
  if (primaryGenre) infoRows.push(['Genre principal', genreName(primaryGenre)]);

  return (
    <div className={`animate-fadeIn ${isPromoter ? 'pb-24 lg:pb-0' : ''}`}>
      {/* En-tête contenu */}
      <header className="border-b th-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="flex flex-col sm:flex-row sm:items-end gap-6">
            {/* Photo */}
            <div className="group shrink-0 w-36 h-36 sm:w-44 sm:h-44 th-bg-elevated border th-border overflow-hidden">
              {artist.profilePhotoUrl ? (
                <img
                  src={artist.profilePhotoUrl}
                  alt={artist.artistName}
                  className="th-photo-duotone w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="th-display-lg th-text-muted select-none">{initial}</span>
                </div>
              )}
            </div>

            {/* Titre + méta */}
            <div className="min-w-0 flex-1">
              <h1 className="th-display-lg break-words">{artist.artistName}</h1>
              {baseParts.length > 0 && (
                <p className="th-mono-meta th-text-secondary mt-3">{baseParts.join(' · ')}</p>
              )}
              {genres.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {genres.map((g) => (
                    <span
                      key={genreName(g)}
                      className={`th-badge ${isPrimaryGenre(g) ? 'border-accent text-accent' : ''}`}
                    >
                      {genreName(g)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Contenu deux colonnes */}
      <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_360px]">
        {/* Colonne principale */}
        <div className="lg:border-r th-border px-4 sm:px-6 lg:px-8 py-10">
          {artist.bio && (
            <section className="animate-fadeInUp">
              <p className="th-kicker mb-3">Bio</p>
              <p className="th-serif-body max-w-[62ch] whitespace-pre-line">{artist.bio}</p>
            </section>
          )}

          {artist.bio && <hr className="border-t th-border my-10" />}

          <section className="animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            <div className="th-section-head mb-5">
              <div>
                <p className="th-kicker th-kicker-accent mb-1">Booking</p>
                <h2 className="th-display-md">Disponibilités</h2>
              </div>
            </div>

            <AvailabilityCalendar
              slots={slots}
              month={calMonth}
              year={calYear}
              onMonthChange={(m, y) => { setCalMonth(m); setCalYear(y); setSelectedSlot(null); }}
              mode="readonly"
              onSlotClick={isPromoter ? (slot) => setSelectedSlot(slot) : null}
            />

            {/* Panneau info slot cliqué : promoteur uniquement */}
            {selectedSlot && isPromoter && (
              <div className="mt-4 border th-border p-4 th-bg-elevated animate-fadeIn">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <span className="th-mono-meta">{slotStatusLabel(selectedSlot.status)}</span>
                    <p className="th-text font-mono text-sm mt-1">
                      {new Date(selectedSlot.startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                      {selectedSlot.startDate !== selectedSlot.endDate && (
                        <> &rarr; {new Date(selectedSlot.endDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedSlot.status === 'available' && (
                      <button onClick={goToBooking} className="th-btn-accent text-xs">
                        Proposer un booking
                      </button>
                    )}
                    <button onClick={() => setSelectedSlot(null)} className="th-btn text-xs">
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Colonne latérale */}
        <aside className="lg:sticky lg:top-0 self-start px-4 sm:px-6 lg:px-8 py-10 space-y-6">
          {/* Bloc booking / conversion */}
          {!(isAuthenticated && !isPromoter) && (
            <div className="border th-border th-bg-elevated p-6">
              <h3 className="th-section-title mb-3">Proposer une date</h3>
              {isPromoter && (
                <>
                  <p className="th-mono-meta mb-4">Invitez cet artiste sur l'un de vos événements.</p>
                  <button onClick={goToBooking} className="th-btn-accent w-full">
                    Proposer un booking
                  </button>
                </>
              )}
              {isVisitor && (
                <>
                  <p className="th-mono-meta mb-4">Créez un compte promoteur pour booker cet artiste.</p>
                  <Link to="/register" className="th-btn w-full text-center">
                    Créer un compte
                  </Link>
                </>
              )}
            </div>
          )}

          {/* Bloc écouter / suivre */}
          {socialEntries.length > 0 && (
            <div className="border th-border th-bg-elevated p-6">
              <h3 className="th-section-title mb-3">Écouter / suivre</h3>
              <ul>
                {socialEntries.map(([platform, url]) => (
                  <li key={platform}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex justify-between items-center border-b th-border py-2.5 font-mono text-sm uppercase tracking-wide th-text hover:text-accent transition-colors duration-150"
                    >
                      <span>{platform}</span>
                      <span aria-hidden="true">&#8599;</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Bloc informations */}
          {infoRows.length > 0 && (
            <div className="border th-border th-bg-elevated p-6">
              <h3 className="th-section-title mb-3">Informations</h3>
              <dl>
                {infoRows.map(([label, value]) => (
                  <div
                    key={label}
                    className="flex justify-between items-baseline gap-4 border-b th-border py-2 font-mono text-sm"
                  >
                    <dt className="th-text-muted uppercase tracking-wide">{label}</dt>
                    <dd className="th-text text-right">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </aside>
      </div>

      {/* Barre booking sticky mobile : promoteur uniquement */}
      {isPromoter && (
        <div className="fixed bottom-0 inset-x-0 z-30 lg:hidden border-t th-border th-bg-surface p-3">
          <button onClick={goToBooking} className="th-btn-accent w-full">
            Proposer un booking
          </button>
        </div>
      )}
    </div>
  );
}
